import { supabase } from '../lib/supabaseClient';
import type { Question } from '../types/questions';

// 定数: トップページの新着表示件数
export const RECENT_QUESTIONS_LIMIT = 5;

/**
 * 承認済みの最新の学習相談を取得
 */
export async function fetchRecentQuestions(limit = RECENT_QUESTIONS_LIMIT): Promise<Question[]> {
  const { data, error } = await supabase
    .from('questions')
    .select('*')
    .eq('status', 'approved')
    .eq('is_published', true) // Phase53: 公開フラグ
    .order('updated_at', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching recent questions:', error);
    throw error;
  }

  return data || [];
}

/**
 * 承認済みの学習相談全件を取得（フィルタリング対応）
 */
export async function fetchQuestions(filters?: {
  subject?: string;
  grade?: string;
  keyword?: string;
}): Promise<Question[]> {
  let query = supabase
    .from('questions')
    .select('*')
    .eq('status', 'approved')
    .eq('is_published', true); // Phase53: 公開フラグ

  // フィルタリング
  if (filters?.subject) {
    query = query.eq('subject', filters.subject);
  }
  if (filters?.grade) {
    query = query.eq('grade', filters.grade);
  }

  query = query.order('is_featured', { ascending: false })
    .order('created_at', { ascending: false });

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching questions:', error);
    throw error;
  }

  let results = data || [];

  // キーワード検索（フロント側でフィルタ）
  if (filters?.keyword) {
    const keyword = filters.keyword.toLowerCase();
    results = results.filter((q) => {
      const titleMatch = q.title.toLowerCase().includes(keyword);
      const bodyMatch = (q.body_public || q.body_raw || '').toLowerCase().includes(keyword);
      return titleMatch || bodyMatch;
    });
  }

  return results;
}

/**
 * IDまたはスラッグで学習相談1件を取得
 */
export async function fetchQuestionById(idOrSlug: string): Promise<Question | null> {
  // まずスラッグで検索
  let { data, error } = await supabase
    .from('questions')
    .select('*')
    .eq('slug', idOrSlug)
    .single();

  // スラッグで見つからなければIDで検索
  if (error && error.code === 'PGRST116') {
    const result = await supabase
      .from('questions')
      .select('*')
      .eq('id', idOrSlug)
      .single();
    
    data = result.data;
    error = result.error;
  }

  if (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    console.error('Error fetching question by id:', error);
    throw error;
  }

  return data;
}

/**
 * 似た相談を取得（同じ教科・近い学年）
 */
export async function fetchSimilarQuestions(
  currentId: string,
  subject?: string | null,
  grade?: string | null,
  limit = 3
): Promise<Question[]> {
  let query = supabase
    .from('questions')
    .select('*')
    .eq('status', 'approved')
    .eq('is_published', true)
    .neq('id', currentId);

  if (subject) {
    query = query.eq('subject', subject);
  }

  query = query.order('created_at', { ascending: false }).limit(limit);

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching similar questions:', error);
    return [];
  }

  return data || [];
}

/**
 * 新規学習相談を投稿
 */
export async function createQuestion(payload: {
  category: string;
  role: string;
  title: string;
  body_raw: string;
  email?: string;
  material_id?: string;
}): Promise<Question> {
  const { data, error } = await supabase
    .from('questions')
    .insert({
      category: payload.category,
      role: payload.role,
      title: payload.title,
      body_raw: payload.body_raw,
      body_public: payload.body_raw, // 同じ内容を保存
      email: payload.email || null,
      material_id: payload.material_id || null,
      status: 'pending', // 承認待ち
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating question:', error);
    throw error;
  }

  return data;
}

/**
 * 承認待ちの学習相談を取得（管理用）
 */
export async function fetchPendingQuestions(limit = 50): Promise<Question[]> {
  const { data, error } = await supabase
    .from('questions')
    .select('*')
    .eq('status', 'pending')
    .order('created_at', { ascending: true })
    .limit(limit);

  if (error) {
    console.error('Error fetching pending questions:', error);
    throw error;
  }

  return data || [];
}

/**
 * 学習相談の承認・編集（管理用）
 */
export async function updateQuestionModeration(
  id: string,
  input: UpdateQuestionModerationInput
): Promise<Question> {
  const updateData: any = {
    updated_at: new Date().toISOString(),
  };

  if (typeof input.status !== 'undefined') {
    updateData.status = input.status;
  }
  if (typeof input.is_published !== 'undefined') {
    updateData.is_published = input.is_published;
  }
  if (typeof input.body_public !== 'undefined') {
    updateData.body_public = input.body_public;
  }

  const { data, error } = await supabase
    .from('questions')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * 全ての学習相談を取得（管理用）
 */
export async function fetchAllQuestions(): Promise<Question[]> {
  const { data, error } = await supabase
    .from('questions')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching all questions:', error);
    throw error;
  }

  return data || [];
}

/**
 * 学習相談のステータスを更新（管理用）
 */
export async function updateQuestionStatus(
  id: string,
  status: 'pending' | 'approved' | 'rejected'
): Promise<Question> {
  const { data, error } = await supabase
    .from('questions')
    .update({ 
      status,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating question status:', error);
    throw error;
  }

  return data;
}

/**
 * 学習相談を削除（管理用）
 */
export async function deleteQuestion(id: string): Promise<void> {
  const { error } = await supabase
    .from('questions')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting question:', error);
    throw error;
  }
}
